// src/components/Forms/TaskForm.jsx
// WP-12 Step I.1: the single task add/edit form, shared by the Activities To-do
// tab (inline) and the Work Queue TASKS subpanel (popover). Self-contained styles
// so it works in both a wide container and a narrow popover (grid collapses).
// Vocabulary matches Activities: Scope = Global / Band / Farm / Site, Target = entity.
import React, { useState } from 'react';
import api from '../../services/api';
import Combobox from '../Combobox';
import { TargetPick } from '../FormPickers';
import { TASK_SEVERITIES, SEVERITY_CFG } from '../../config/taskSeverity';
import { DOMAIN_LABELS } from '../../config/constants';
import { DrawerActions } from '../../shell/DrawerBody';
import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
import { TextInput } from '../inputs/Input';

// scope: { mode, id } context that seeds a NEW task's scope (from the current view
//   or the To-do tab's chip). initial: a task row for edit mode.
// defaultDomain: pre-select a domain (e.g. 'procurement' from the Procurement WorkQueue).
export default function TaskForm({ refData, scope = { mode: 'global', id: '' }, initial, onSaved, onCancel, defaultDomain }) {
  const { t } = useTranslation('dataEntry');
  const isEdit = !!initial;
  const seedScopeType = initial ? (initial.scope_type || 'global')
    : (['band', 'farm', 'site'].includes(scope.mode) && scope.id ? scope.mode : 'global');
  const seedScopeId = initial ? (initial.scope_id || '') : (seedScopeType !== 'global' ? scope.id : '');
  const [title, setTitle] = useState(initial?.title || '');
  const [severity, setSeverity] = useState(initial?.severity || 'normal');
  const [dueDate, setDueDate] = useState(initial?.due_date || '');
  const [scopeType, setScopeType] = useState(seedScopeType);
  const [scopeId, setScopeId] = useState(seedScopeId);
  const [domain, setDomain] = useState(initial?.domain || defaultDomain || null);
  const [notes, setNotes] = useState(initial?.notes || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const targetOpts = scopeType === 'band' ? (refData.bands || []) : scopeType === 'farm' ? (refData.farms || []) : scopeType === 'site' ? (refData.sites || []) : [];

  async function submit() {
    setErr('');
    if (!title.trim()) { setErr(t('deUi.titleIsRequired')); return; }
    if (scopeType !== 'global' && !scopeId) { setErr(t('deUi.pickATargetForThe')); return; }
    setBusy(true);
    try {
      const body = {
        title: title.trim(), severity, notes: notes || null, due_date: dueDate || null,
        scope_type: scopeType === 'global' ? null : scopeType,
        scope_id: scopeType === 'global' ? null : scopeId,
        domain: domain || null,
      };
      if (isEdit) await api.updateTask(initial.id, body); else await api.createTask(body);
      onSaved();
    } catch (e) { setErr(e.message); setBusy(false); }
  }

  return (
    <div>
      <div style={grid}>
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>{t('common:alertsUi.title')}</label>
          <TextInput value={title} onChange={e => setTitle(e.target.value)} style={inp} placeholder={t('deUi.whatNeedsDoing')} autoFocus /></div>
        {/* All dropdowns use the same Combobox control for a coherent look. */}
        <div><label style={lbl}>{t('common:alertsPlatform.feedAlert.severity')}</label>
          <Combobox options={TASK_SEVERITIES.map(s => ({ value: s, label: SEVERITY_CFG[s].label }))}
            value={severity} onChange={v => setSeverity(v || 'normal')} allowClear={false} placeholder={t('common:alertsPlatform.feedAlert.severity')} /></div>
        <div><label style={lbl}>{t('deUi.dueDate')}</label>
          <TextInput type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={inp} /></div>
        <div><label style={lbl}>{t('fields.scope')}</label>
          <Combobox options={SCOPE_OPTS} value={scopeType}
            onChange={v => { setScopeType(v || 'global'); setScopeId(''); }} allowClear={false} placeholder={t('fields.scope')} /></div>
        {scopeType !== 'global' && (
          <div><label style={lbl}>{t('parameters.targetCol')}</label>
            <TargetPick type={scopeType} items={targetOpts} value={scopeId} onChange={v => setScopeId(v || '')} placeholder={t('deUi.select', { scopeType })} /></div>
        )}
        <div><label style={lbl}>{t('deUi.domainOptional')}</label>
          <Combobox options={[{ value: '', label: t('deUi.noDomainGeneral') }, ...Object.entries(DOMAIN_LABELS).map(([v, l]) => ({ value: v, label: l }))]}
            value={domain || ''} onChange={v => setDomain(v || null)} allowClear={false} placeholder={t('deUi.domain')} /></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>{t('fields.notes')}</label>
          <TextInput value={notes} onChange={e => setNotes(e.target.value)} style={inp} placeholder={t('fl.optional_2')} /></div>
      </div>
      {err && <div style={errStyle}>{err}</div>}
      <DrawerActions legacyRow="act-row mt12" legacyClose={<button className="btn-ghost" onClick={onCancel}>{t('actions.cancel')}</button>}>
        {inFoot => <button className={inFoot ? 'btn success' : 'btn-primary'} onClick={submit} disabled={busy || !title.trim()}>{busy ? t('deUi.working') : isEdit ? t('actions.save') : t('deUi.addTask')}</button>}
      </DrawerActions>
    </div>
  );
}

const SCOPE_OPTS = [{ value: 'global', get label() { return i18n.t('dataEntry:scope.global'); } }, { value: 'band', get label() { return i18n.t('dataEntry:fields.band'); } }, { value: 'farm', get label() { return i18n.t('dataEntry:parameters.scope.farm'); } }, { value: 'site', get label() { return i18n.t('dataEntry:parameters.scope.location'); } }];
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 };
const lbl = { display: 'block', fontSize: 9, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--label)', marginBottom: 6 };
const inp = { padding: '8px 12px', border: '1px solid var(--line)', borderRadius: 8, fontSize: 13, background: '#fff', color: 'var(--ink)', width: '100%', boxSizing: 'border-box' };
const errStyle = { color: 'var(--red)', background: 'var(--red-bg)', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginTop: 10 };
